import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../services/api";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Pagination,
  Collapse,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { COMPLIANCE_CAN_MANAGE_MASTERDATA } from "../app_props";

const STATUS_COLOR = {
  SUCCESS: "success",
  PARTIAL: "warning",
  FAILED: "error",
};
const IMPORT_OUTCOME_COLOR = {
  IMPORTED: "success",
  SKIPPED: "default",
  DRY_RUN: "info",
  FAILED: "error",
};

function ChangeReportRow({ version }) {
  const [open, setOpen] = useState(false);
  let entries = [];
  try {
    entries = version.changeReport ? JSON.parse(version.changeReport) : [];
  } catch (e) {
    // leave empty if unparsable
  }

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            disabled={entries.length === 0}
          >
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell>{version.packageCode}</TableCell>
        <TableCell>{version.version}</TableCell>
        <TableCell>
          <Chip
            size="small"
            label={version.status}
            color={STATUS_COLOR[version.status] || "default"}
          />
        </TableCell>
        <TableCell>
          {version.importedAt
            ? new Date(version.importedAt).toLocaleString()
            : "—"}
        </TableCell>
        <TableCell>{version.importedBy || "—"}</TableCell>
        <TableCell>{version.regulationCount ?? 0}</TableCell>
        <TableCell>{version.standardCount ?? 0}</TableCell>
        <TableCell>{version.requirementCount ?? 0}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={9}>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{ m: 1, backgroundColor: "#f9fafb", borderRadius: 1, p: 1 }}
            >
              {entries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No change details recorded for this import.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Action</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Canonical key</TableCell>
                      <TableCell>Changed fields</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((e, i) => (
                      <TableRow key={i}>
                        <TableCell>{e.action}</TableCell>
                        <TableCell>{e.recordType || "—"}</TableCell>
                        <TableCell>{e.title || "—"}</TableCell>
                        <TableCell>{e.canonicalKey}</TableCell>
                        <TableCell>
                          {(e.changedFields || []).join(", ") || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function MasterRegistry() {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const size = 20;

  const user = JSON.parse(sessionStorage.getItem("userInfo") || "null");
  const canImport =
    user?.authorities?.includes(COMPLIANCE_CAN_MANAGE_MASTERDATA) || false;

  async function load(p = page) {
    setLoading(true);
    setError("");
    try {
      const data = await apiGet(
        `/api/master-registry/versions?page=${p}&size=${size}`,
      );
      const content = Array.isArray(data) ? data : data.content || [];
      setVersions(content);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      console.error("Failed to load Master Registry import history", err);
      setError("Failed to load Master Registry import history: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(page);
  }, [page]);

  async function triggerImport() {
    setImporting(true);
    setError("");
    setImportResults(null);
    try {
      const results = await apiPost(
        "/api/master-registry/import?taskPolicy=KEEP",
      );
      setImportResults(results);
      setPage(0);
      await load(0);
    } catch (err) {
      console.error("Master Registry import failed", err);
      setError("Import failed: " + err.message);
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="h-[89.8vh]">
      <Box sx={{ m: 1 }}>
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6">
                Compliance Master — Import History
              </Typography>
              {canImport && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={triggerImport}
                  disabled={importing}
                >
                  {importing ? "Importing..." : "Import Now"}
                </Button>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Every version pulled from VISTAAR's Compliance Master into this
              compliance-api instance, newest first — via the{" "}
              <code>--import-master-registry</code> CLI command or the "Import
              Now" button above.
            </Typography>
            {error && (
              <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                {error}
              </Typography>
            )}
            {importResults && (
              <Box sx={{ mb: 2 }}>
                {importResults.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No subscribed packages have a published version to import.
                  </Typography>
                ) : (
                  importResults.map((r, i) => (
                    <Chip
                      key={i}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                      color={IMPORT_OUTCOME_COLOR[r.outcome] || "default"}
                      label={`${r.packageCode} ${r.versionLabel || ""}: ${r.outcome}${r.message ? " — " + r.message : ""}`}
                    />
                  ))
                )}
              </Box>
            )}
            <Box sx={{ maxHeight: "79vh", overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Package</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Imported at</TableCell>
                    <TableCell>Imported by</TableCell>
                    <TableCell>Regulations</TableCell>
                    <TableCell>Standards</TableCell>
                    <TableCell>Requirements</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && versions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary">
                          No imports recorded yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {versions.map((v) => (
                    <ChangeReportRow key={v.id} version={v} />
                  ))}
                </TableBody>
              </Table>
            </Box>
            {loading && <div style={{ padding: 12 }}>Loading...</div>}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  size="small"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}
